import { useEffect, useState, useMemo } from "react";
import api, { errMsg, formatTL, formatDate } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Pencil, Trash2, HandCoins, GitMerge, X, CheckCircle2, AlertTriangle, RefreshCw, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({
  invoice_no: "", company_id: "", company_name: "", vehicle_id: "",
  date: new Date().toISOString().slice(0, 10), amount: "", status: "issued", notes: "",
});

export default function SalesInvoicesPage() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const [showCollect, setShowCollect] = useState(false);
  const [collectForm, setCollectForm] = useState({ date: new Date().toISOString().slice(0,10), payment_method: "", note: "" });
  const [collectCustom, setCollectCustom] = useState(false);  // per-invoice amount editing
  const [collectAmounts, setCollectAmounts] = useState({});   // { invoice_id: custom_amount }
  const [collecting, setCollecting] = useState(false);

  const [showDedupe, setShowDedupe] = useState(false);
  const [dedupe, setDedupe] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [keeperMap, setKeeperMap] = useState({});
  const [dedupeApplying, setDedupeApplying] = useState(false);

  // Cari sync state
  const [showSync, setShowSync] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncApplying, setSyncApplying] = useState(false);

  // Cari Excel bulk upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const runUploadPreview = async () => {
    if (!uploadFile) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("preview", "true");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/companies/upload-cari-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPreview(data);
      toast.success(`${data.totals?.invoice_count || 0} fatura + ${data.totals?.payment_count || 0} ödeme okundu`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadLoading(false); }
  };

  const runUploadCommit = async () => {
    if (!uploadFile) return;
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("preview", "false");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/companies/upload-cari-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.company?.name}: ${data.invoices_inserted} fatura, ${data.payments_inserted} ödeme eklendi${data.company_created ? " (yeni cari)" : ""}`);
      setShowUpload(false);
      setUploadFile(null);
      setUploadPreview(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploadLoading(false); }
  };

  const load = async () => {
    try {
      const [inv, c, v] = await Promise.all([
        api.get("/sales-invoices"),
        api.get("/companies"),
        api.get("/vehicles"),
      ]);
      setRows(inv.data || []);
      setCompanies(c.data || []);
      setVehicles(v.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const today = new Date();
    const sixtyDaysAgo = new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000);
    return rows.filter(r => {
      if (statusFilter === "overdue") {
        const isPending = r.status !== "paid" && r.status !== "collected";
        const invDate = new Date(r.date);
        if (!isPending || isNaN(invDate) || invDate > sixtyDaysAgo) return false;
      } else if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!query) return true;
      return (r.company_name || "").toLowerCase().includes(query)
        || (r.invoice_no || "").toLowerCase().includes(query);
    });
  }, [rows, q, statusFilter]);

  const vehiclePlate = (id) => vehicles.find(x => x.id === id)?.plate || "-";

  const openCreate = () => { setEditing(null); setForm(empty()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      invoice_no: r.invoice_no || "", company_id: r.company_id || "",
      company_name: r.company_name || "", vehicle_id: r.vehicle_id || "",
      date: r.date || "", amount: r.amount || "", status: r.status || "issued", notes: r.notes || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editing) {
        await api.put(`/sales-invoices/${editing.id}`, payload);
        toast.success("Fatura güncellendi");
      } else {
        await api.post("/sales-invoices", payload);
        toast.success("Fatura eklendi");
      }
      setShowForm(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`${r.invoice_no || "Fatura"} silinsin mi?`)) return;
    try { await api.delete(`/sales-invoices/${r.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.id)));
  };

  const openBulkCollect = () => {
    if (selected.size === 0) { toast.error("En az bir fatura seçin"); return; }
    // Init collectAmounts with each selected invoice's original amount
    const amounts = {};
    for (const r of filtered) {
      if (selected.has(r.id)) amounts[r.id] = r.amount;
    }
    setCollectAmounts(amounts);
    setCollectCustom(false);
    setShowCollect(true);
  };

  const submitCollect = async () => {
    setCollecting(true);
    try {
      const payload = { ...collectForm };
      if (collectCustom) {
        payload.invoices = Array.from(selected).map(id => ({ id, amount: Number(collectAmounts[id]) || 0 }));
      } else {
        payload.invoice_ids = Array.from(selected);
      }
      const { data } = await api.post("/sales-invoices/bulk-collect", payload);
      const partialCount = (data.details || []).filter(d => d.is_partial).length;
      toast.success(`${data.created} fatura tahsil edildi${partialCount ? ` (${partialCount} kısmi)` : ""} · Toplam: ${formatTL(data.total_amount)}`);
      setShowCollect(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCollecting(false); }
  };

  const openDedupe = async () => {
    setShowDedupe(true); setDedupeLoading(true); setDedupe(null);
    try {
      const { data } = await api.get("/admin/dedupe-companies/preview");
      setDedupe(data);
      const map = {};
      for (const g of data.groups) map[g.key] = g.suggested_keeper_id;
      setKeeperMap(map);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDedupeLoading(false); }
  };

  const applyDedupe = async () => {
    if (!dedupe || dedupe.group_count === 0) return;
    if (!window.confirm(`${dedupe.group_count} gruptan ${dedupe.duplicates_to_remove} mükerrer firma kaydı kaldırılacak. Satış faturaları ve gelen ödemeler tutulan firmaya taşınacak. Onaylıyor musunuz?`)) return;
    setDedupeApplying(true);
    try {
      const { data } = await api.post("/admin/dedupe-companies", { keeper_map: keeperMap });
      toast.success(`${data.duplicates_removed} mükerrer birleştirildi · ${data.sales_reassigned} fatura + ${data.incoming_reassigned} ödeme taşındı`);
      setShowDedupe(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDedupeApplying(false); }
  };

  const openSync = async () => {
    setShowSync(true); setSyncLoading(true); setSyncPreview(null);
    try {
      const { data } = await api.post("/sales-invoices/sync-from-cari", { dry_run: true });
      setSyncPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSyncLoading(false); }
  };

  const applySync = async () => {
    if (!syncPreview || syncPreview.changed === 0) { setShowSync(false); return; }
    if (!window.confirm(`${syncPreview.changed} faturanın statüsü Firma Cari tahsilatlarıyla eşitlenecek: ${syncPreview.counts.paid} tam tahsil, ${syncPreview.counts.partial} kısmi, ${syncPreview.counts.issued} bekleyen. Onaylıyor musunuz?`)) return;
    setSyncApplying(true);
    try {
      const { data } = await api.post("/sales-invoices/sync-from-cari", { dry_run: false });
      toast.success(`${data.changed} fatura senkronlandı (${data.counts.paid} paid · ${data.counts.partial} partial · ${data.counts.issued} issued)`);
      setShowSync(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSyncApplying(false); }
  };

  const totalSelected = filtered.filter(r => selected.has(r.id)).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalIssued = filtered.filter(r => r.status !== "paid" && r.status !== "collected").reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalCollected = filtered.filter(r => r.status === "paid" || r.status === "collected").reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const totalOverdue = useMemo(() => {
    const cutoff = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
    return rows.reduce((s, r) => {
      const isPending = r.status !== "paid" && r.status !== "collected";
      const d = new Date(r.date);
      if (isPending && !isNaN(d) && d < cutoff) return s + (Number(r.amount) || 0);
      return s;
    }, 0);
  }, [rows]);

  return (
    <>
      <PageHeader
        title="Kesilen Faturalar"
        subtitle="Firmalara kestiğiniz satış faturaları. Birden fazla fatura seçip toplu tahsilat yapabilir, mükerrer firma kayıtlarını birleştirebilirsiniz."
        testid="sales-invoices-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="btn-open-upload" onClick={() => { setUploadPreview(null); setUploadFile(null); setShowUpload(true); }} className="inline-flex items-center gap-2 border border-[#0020C2] text-[#0020C2] px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0020C2] hover:text-white transition-colors">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Toplu Fatura Yükle (Excel)
            </button>
            <button data-testid="btn-open-dedupe-companies" onClick={openDedupe} className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#6D28D9]">
              <GitMerge className="w-3.5 h-3.5" /> Mükerrer Firmaları Birleştir
            </button>
            <button data-testid="btn-open-sync" onClick={openSync} className="inline-flex items-center gap-2 bg-[#0020C2] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0018A0]">
              <RefreshCw className="w-3.5 h-3.5" /> Cari ile Eşitle
            </button>
            <button data-testid="btn-bulk-collect" onClick={openBulkCollect} disabled={selected.size === 0} className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#15803D] disabled:opacity-50 disabled:cursor-not-allowed">
              <HandCoins className="w-3.5 h-3.5" /> Toplu Tahsil Et ({selected.size})
            </button>
            <button data-testid="btn-add-sales" onClick={openCreate} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0020C2]">
              <Plus className="w-3.5 h-3.5" /> Yeni Fatura
            </button>
          </div>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura Sayısı</div><div className="num font-bold text-xl mt-1">{filtered.length}</div></div>
        <div className="bg-white border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">Kesildi (Bekleyen)</div><div className="num font-bold text-xl mt-1 text-[#F59E0B]">{formatTL(totalIssued)}</div></div>
        <div className="bg-white border border-[#DC2626] p-3 cursor-pointer" onClick={() => setStatusFilter("overdue")} title="Sadece vadesi geçmişleri göster"><div className="text-[10px] uppercase tracking-widest text-[#DC2626] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Vadesi Geçmiş (60+g)</div><div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(totalOverdue)}</div></div>
        <div className="bg-white border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">Tahsil Edildi</div><div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(totalCollected)}</div></div>
        <div className="bg-white border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">Seçili Toplam</div><div className="num font-bold text-xl mt-1 text-[#0020C2]">{formatTL(totalSelected)}</div></div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-3 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <input data-testid="search-invoices" value={q} onChange={e => setQ(e.target.value)} placeholder="Firma veya fatura no ara..." className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-[#E5E5E5]" />
        <select data-testid="filter-status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1.5 text-xs border border-[#E5E5E5]">
          <option value="all">Tümü</option>
          <option value="issued">Kesildi (Bekleyen)</option>
          <option value="overdue">Vadesi Geçmiş (60+ gün)</option>
          <option value="paid">Tahsil Edildi</option>
          <option value="partial">Kısmi Tahsil</option>
          <option value="collected">Tahsil Edildi (eski)</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                <th className="px-3 py-2 text-left w-10">
                  <input data-testid="sel-all" type="checkbox" checked={selected.size > 0 && selected.size === filtered.length} onChange={toggleAll} />
                </th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">İşlem</th>
                {["Fatura No","Tarih","Firma","Araç","Tutar","Durum"].map((h, i) => (
                  <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-10 text-center text-sm text-neutral-500">Kayıt bulunamadı</td></tr>
              ) : filtered.map(r => {
                const isPaid = r.status === "paid" || r.status === "collected";
                const isPartial = r.status === "partial";
                const isPending = !isPaid && !isPartial;
                const isOverdue = isPending && (Date.now() - new Date(r.date).getTime()) > 60 * 24 * 60 * 60 * 1000;
                const badgeClass = isPaid ? "bg-[#DCFCE7] text-[#16A34A]" : isPartial ? "bg-[#FEF3C7] text-[#F59E0B]" : isOverdue ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#FEF3C7] text-[#92400E]";
                const badgeText = isPaid ? "Tahsil Edildi" : isPartial ? "Kısmi" : isOverdue ? "Vadesi Geçmiş" : "Bekliyor";
                return (
                  <tr key={r.id} className="border-b border-[#F5F5F5]" data-testid={`sales-row-${r.id}`}>
                    <td className="px-3 py-2"><input data-testid={`sel-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} disabled={isPaid} title={isPaid ? "Zaten tahsil edildi" : ""} /></td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#0020C2] hover:text-white hover:border-[#0020C2]" title="Düzenle"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`del-${r.id}`} onClick={() => deleteRow(r)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626]" title="Sil"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                    <td className="px-3 py-2 num text-neutral-700">{r.invoice_no || "-"}</td>
                    <td className="px-3 py-2 num text-neutral-600">{formatDate(r.date)}</td>
                    <td className="px-3 py-2 font-semibold">{r.company_name || "-"}</td>
                    <td className="px-3 py-2 num text-neutral-600">{r.vehicle_id ? vehiclePlate(r.vehicle_id) : "-"}</td>
                    <td className="px-3 py-2 text-right num text-[#16A34A] font-semibold">{formatTL(r.amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${badgeClass}`}>{badgeText}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div className="font-heading font-bold text-lg">{editing ? "Fatura Düzenle" : "Yeni Fatura"}</div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura No *</label><input required value={form.invoice_no} onChange={e => setForm({...form, invoice_no: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label><input type="date" required value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" /></div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma *</label>
                <input list="company-list" required value={form.company_name} onChange={e => { const nm = e.target.value; const m = companies.find(c => (c.name || "").trim().toLowerCase() === nm.trim().toLowerCase()); setForm({...form, company_name: nm, company_id: m?.id || ""}); }} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                <datalist id="company-list">{companies.map(c => <option key={c.id} value={c.name} />)}</datalist>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label><input type="number" step="any" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Durum</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                    <option value="issued">Kesildi</option><option value="paid">Tahsil Edildi</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç (ops.)</label>
                <select value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="">- Seçin -</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} {v.model && `— ${v.model}`}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label><textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" /></div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk collect modal */}
      {showCollect && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowCollect(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu Tahsilat</div>
                <div className="font-heading font-bold text-lg">{selected.size} fatura · {formatTL(collectCustom ? Object.entries(collectAmounts).filter(([id]) => selected.has(id)).reduce((s,[,v]) => s + (Number(v)||0), 0) : totalSelected)}</div>
              </div>
              <button onClick={() => setShowCollect(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-[#F0FDF4] border border-[#16A34A] p-3 text-xs text-[#166534]">
                Her seçili fatura için ayrı bir <b>Gelen Ödeme</b> kaydı oluşturulur. Tam tutar tahsil edilirse fatura &quot;paid&quot;, kısmi ise &quot;partial&quot; olarak işaretlenir.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label><input data-testid="collect-date" type="date" required value={collectForm.date} onChange={e => setCollectForm({...collectForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ödeme Yolu</label><input placeholder="Havale/EFT, Nakit..." value={collectForm.payment_method} onChange={e => setCollectForm({...collectForm, payment_method: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" /></div>
              </div>
              <div><label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not</label><input placeholder="Toplu tahsilat" value={collectForm.note} onChange={e => setCollectForm({...collectForm, note: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" /></div>

              <div className="flex items-center gap-2 pt-2">
                <input data-testid="collect-custom-toggle" id="collect-custom" type="checkbox" checked={collectCustom} onChange={e => setCollectCustom(e.target.checked)} />
                <label htmlFor="collect-custom" className="text-sm font-semibold cursor-pointer">Farklı Tutarla Tahsil Et (iskonto / kısmi ödeme)</label>
              </div>

              {collectCustom && (
                <div className="border border-[#E5E5E5] rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura No</th>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Firma</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura Tutarı</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tahsil Edilecek</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.filter(r => selected.has(r.id)).map(r => {
                        const custom = Number(collectAmounts[r.id]) || 0;
                        const diff = custom - (Number(r.amount) || 0);
                        return (
                          <tr key={r.id} className="border-b border-[#F5F5F5]">
                            <td className="px-3 py-2 num text-neutral-700">{r.invoice_no}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate" title={r.company_name}>{r.company_name}</td>
                            <td className="px-3 py-2 text-right num text-neutral-600">{formatTL(r.amount)}</td>
                            <td className="px-3 py-2 text-right">
                              <input
                                data-testid={`custom-amount-${r.id}`}
                                type="number" step="any"
                                value={collectAmounts[r.id] ?? ""}
                                onChange={e => setCollectAmounts({...collectAmounts, [r.id]: e.target.value})}
                                className="w-32 px-2 py-1 border border-[#E5E5E5] text-sm num text-right"
                              />
                            </td>
                            <td className={`px-3 py-2 text-right num font-semibold ${diff < -0.5 ? "text-[#F59E0B]" : diff > 0.5 ? "text-[#DC2626]" : "text-neutral-400"}`}>
                              {diff !== 0 ? (diff > 0 ? "+" : "") + formatTL(diff) : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]">
              <button onClick={() => setShowCollect(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5]">Vazgeç</button>
              <button data-testid="collect-submit" disabled={collecting} onClick={submitCollect} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-50">{collecting ? "İşleniyor..." : `${selected.size} Faturayı Tahsil Et`}</button>
            </div>
          </div>
        </div>
      )}

      {/* Dedupe modal */}
      {showDedupe && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowDedupe(false)}>
          <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Mükerrer Firma Birleştir</div>
                <div className="font-heading font-bold text-lg">Örn: KODMARK ETİKET (2 kayıt) → 1 kayıt</div>
              </div>
              <button onClick={() => setShowDedupe(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {dedupeLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Analiz ediliyor...</div>
              ) : !dedupe ? null : dedupe.group_count === 0 ? (
                <div className="text-center py-10 border border-dashed border-[#16A34A] bg-[#F0FDF4]">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-[#16A34A] mb-2" />
                  <div className="text-sm font-semibold text-[#166534]">Mükerrer firma bulunamadı.</div>
                  <div className="text-xs text-neutral-500 mt-1">Toplam {dedupe.total_companies} firma kaydı temiz.</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-[#F5F3FF] border border-[#7C3AED] p-3"><div className="text-[10px] uppercase tracking-widest text-[#7C3AED]">Mükerrer Grup</div><div className="num font-bold text-xl mt-1 text-[#7C3AED]">{dedupe.group_count}</div></div>
                    <div className="bg-[#FEE2E2] border border-[#DC2626] p-3"><div className="text-[10px] uppercase tracking-widest text-[#DC2626]">Silinecek Kayıt</div><div className="num font-bold text-xl mt-1 text-[#DC2626]">{dedupe.duplicates_to_remove}</div></div>
                    <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Firma</div><div className="num font-bold text-xl mt-1">{dedupe.total_companies}</div><div className="text-[10px] text-neutral-500">İşlem sonrası: {dedupe.total_companies - dedupe.duplicates_to_remove}</div></div>
                  </div>
                  <div className="space-y-4">
                    {dedupe.groups.map(g => (
                      <div key={g.key} className="border border-[#E5E5E5] rounded-md overflow-hidden">
                        <div className="bg-[#FAFAFA] px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">Grup: <span className="text-[#7C3AED]">{g.key}</span> · {g.count} kayıt</div>
                          <div className="text-[10px] text-neutral-500">Tutulacak kaydı seçin ↓</div>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-[#F5F5F5]">
                            <tr>
                              <th className="px-2 py-2 w-8"></th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">İsim</th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">VKN</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tahsilat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.records.map(r => {
                              const isKeeper = keeperMap[g.key] === r.id;
                              return (
                                <tr key={r.id} className={`border-b border-[#F5F5F5] ${isKeeper ? "bg-[#DCFCE7]" : ""}`}>
                                  <td className="px-2 py-2"><input type="radio" name={`keep-${g.key}`} checked={isKeeper} onChange={() => setKeeperMap({ ...keeperMap, [g.key]: r.id })} /></td>
                                  <td className="px-3 py-2"><div className={`font-semibold ${isKeeper ? "text-[#16A34A]" : ""}`}>{r.name}{isKeeper && <span className="ml-2 text-[9px] uppercase tracking-widest bg-[#16A34A] text-white px-1.5 py-0.5">TUTULACAK</span>}</div><div className="text-[10px] text-neutral-500">{r.category || "-"}</div></td>
                                  <td className="px-3 py-2 num text-[10px] text-neutral-600">{r.tax_no || "-"}</td>
                                  <td className="px-3 py-2 text-right"><div className="num font-semibold">{r.sales_count}</div><div className="text-[9px] text-neutral-500">{formatTL(r.sales_total)}</div></td>
                                  <td className="px-3 py-2 text-right"><div className="num font-semibold text-[#16A34A]">{r.incoming_count}</div><div className="text-[9px] text-neutral-500">{formatTL(r.incoming_total)}</div></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {dedupe && dedupe.group_count > 0 && (
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <button onClick={() => setShowDedupe(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="dedupe-apply-companies" disabled={dedupeApplying} onClick={applyDedupe} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-50">{dedupeApplying ? "Uygulanıyor..." : `${dedupe.duplicates_to_remove} Mükerrer Kaydı Birleştir`}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync from Cari modal */}
      {showSync && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowSync(false)}>
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Firma Cari ile Eşitleme</div>
                <div className="font-heading font-bold text-lg">Tahsilat verilerine göre kesilen fatura statüleri</div>
                <div className="text-xs text-neutral-500 mt-0.5">Firma cari tahsilatları baz alınır. Fatura tamamen tahsil edildi ise &quot;paid&quot;, kısmi ise &quot;partial&quot;, hiç tahsilat yoksa &quot;issued&quot; olarak işaretlenir.</div>
              </div>
              <button onClick={() => setShowSync(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {syncLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Analiz ediliyor...</div>
              ) : !syncPreview ? null : syncPreview.changed === 0 ? (
                <div className="text-center py-10 border border-dashed border-[#16A34A] bg-[#F0FDF4]">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-[#16A34A] mb-2" />
                  <div className="text-sm font-semibold text-[#166534]">Tüm faturalar cari ile eşleşmiş.</div>
                  <div className="text-xs text-neutral-500 mt-1">Toplam {syncPreview.total_invoices} fatura kontrol edildi, değişiklik gerekmiyor.</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="bg-[#EFF6FF] border border-[#0020C2] p-3"><div className="text-[10px] uppercase tracking-widest text-[#0020C2]">Değişecek</div><div className="num font-bold text-xl mt-1 text-[#0020C2]">{syncPreview.changed}</div></div>
                    <div className="bg-[#DCFCE7] border border-[#16A34A] p-3"><div className="text-[10px] uppercase tracking-widest text-[#166534]">→ Tahsil Edildi</div><div className="num font-bold text-xl mt-1 text-[#16A34A]">{syncPreview.counts.paid || 0}</div></div>
                    <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3"><div className="text-[10px] uppercase tracking-widest text-[#92400E]">→ Kısmi</div><div className="num font-bold text-xl mt-1 text-[#F59E0B]">{syncPreview.counts.partial || 0}</div></div>
                    <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3"><div className="text-[10px] uppercase tracking-widest text-neutral-500">→ Bekliyor</div><div className="num font-bold text-xl mt-1 text-neutral-500">{syncPreview.counts.issued || 0}</div></div>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-2">Değişiklik Detayları (ilk 200)</div>
                  <div className="border border-[#E5E5E5] rounded-md overflow-hidden">
                    <div className="overflow-x-auto max-h-[50vh]">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5] sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura No</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Firma</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tahsilat</th>
                            <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Eski → Yeni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.changes.map(c => {
                            const badgeCls = c.new_status === "paid" ? "bg-[#DCFCE7] text-[#16A34A]" : c.new_status === "partial" ? "bg-[#FEF3C7] text-[#F59E0B]" : "bg-[#F5F5F5] text-neutral-500";
                            return (
                              <tr key={c.invoice_id} className="border-b border-[#F5F5F5]">
                                <td className="px-3 py-2 num text-neutral-700">{c.invoice_no}</td>
                                <td className="px-3 py-2 max-w-[220px] truncate" title={c.company_name}>{c.company_name}</td>
                                <td className="px-3 py-2 text-right num text-neutral-600">{formatTL(c.amount)}</td>
                                <td className="px-3 py-2 text-right num text-[#16A34A] font-semibold">{formatTL(c.collected)}</td>
                                <td className="px-3 py-2 text-center"><span className="text-neutral-400">{c.old_status}</span> → <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${badgeCls}`}>{c.new_status}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
            {syncPreview && syncPreview.changed > 0 && (
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <button onClick={() => setShowSync(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="sync-apply-btn" disabled={syncApplying} onClick={applySync} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0] disabled:opacity-50">{syncApplying ? "Uygulanıyor..." : `${syncPreview.changed} Faturayı Senkronla`}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cari Excel Bulk Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Kesilen Faturalar — Cari Excel Yükleme</div>
              </div>
              <button data-testid="upload-close-btn" onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-2">📋 Beklenen Excel formatı:</div>
                <div className="space-y-1">
                  <div><span className="font-semibold">Üst kısım (B/C sütunları):</span> FİRMA · ADRES · VD · VD NO · YETKİLİ KİŞİ · TELEFON · MAIL · VADE</div>
                  <div><span className="font-semibold">Tablo:</span> TARİH · FATURA · FATURA NO · ÖDEME · AÇIKLAMA</div>
                </div>
                <div className="mt-2 text-neutral-500">Sistem: Firmayı otomatik oluşturur/günceller. FATURA&gt;0 satırlar <span className="font-semibold">Satış Faturası</span>, ÖDEME&gt;0 satırlar <span className="font-semibold">Gelen Ödeme</span> olur (AÇIKLAMA = banka/ödeme yolu). Mükerrer fatura no ve aynı tarih+tutardaki ödemeler atlanır.</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                <input data-testid="upload-file-input" type="file" accept=".xlsx" onChange={e => { setUploadFile(e.target.files?.[0] || null); setUploadPreview(null); }} className="mt-1.5 w-full text-sm" />
              </div>

              {!uploadPreview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button data-testid="upload-preview-btn" onClick={runUploadPreview} disabled={uploadLoading || !uploadFile}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40">
                    {uploadLoading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {uploadPreview && (
                <div className="space-y-4">
                  <div className="border border-[#E5E5E5] rounded p-4 bg-[#FAFAFA]">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Firma Bilgileri</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div><span className="text-neutral-500">Ad:</span> <span className="font-semibold">{uploadPreview.company?.name}</span></div>
                      <div><span className="text-neutral-500">Vade:</span> <span className="font-semibold">{uploadPreview.company?.payment_terms || "-"}</span></div>
                      <div><span className="text-neutral-500">VD:</span> {uploadPreview.company?.tax_office || "-"}</div>
                      <div><span className="text-neutral-500">VD No:</span> <span className="num">{uploadPreview.company?.tax_no || "-"}</span></div>
                      <div><span className="text-neutral-500">Yetkili:</span> {uploadPreview.company?.contact_person || "-"}</div>
                      <div><span className="text-neutral-500">Telefon:</span> <span className="num">{uploadPreview.company?.phone || "-"}</span></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura</div>
                      <div className="num font-bold text-lg">{uploadPreview.totals?.invoice_count || 0}</div>
                      <div className="text-[11px] text-neutral-500 num">{formatTL(uploadPreview.totals?.invoice_total || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödeme</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{uploadPreview.totals?.payment_count || 0}</div>
                      <div className="text-[11px] text-[#16A34A] num">{formatTL(uploadPreview.totals?.payment_total || 0)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3 md:col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bakiye (Fatura − Ödeme)</div>
                      <div className={`num font-bold text-lg ${Math.abs(uploadPreview.totals?.balance || 0) > 1 ? "text-[#F59E0B]" : "text-[#16A34A]"}`}>
                        {formatTL(uploadPreview.totals?.balance || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#E5E5E5] rounded overflow-hidden max-h-[400px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          {["Tarih","Tür","Fatura No / Banka","Tutar (₺)"].map((h, i) => (
                            <th key={h} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...(uploadPreview.invoices || []).map(x => ({ ...x, _type: "inv" })),
                          ...(uploadPreview.payments || []).map(x => ({ ...x, _type: "pay" })),
                        ].sort((a,b) => (a.date || "").localeCompare(b.date || "")).map((r, i) => (
                          <tr key={i} className="border-t border-[#F5F5F5]">
                            <td className="px-2 py-1.5 num">{r.date}</td>
                            <td className="px-2 py-1.5">
                              <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${r._type === "inv" ? "bg-[#0020C2]" : "bg-[#16A34A]"}`}>
                                {r._type === "inv" ? "Fatura" : "Ödeme"}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 num text-neutral-700">{r._type === "inv" ? r.invoice_no : r.payment_method}</td>
                            <td className={`px-2 py-1.5 num text-right font-semibold ${r._type === "inv" ? "" : "text-[#16A34A]"}`}>{formatTL(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setUploadPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button data-testid="upload-commit-btn" onClick={runUploadCommit} disabled={uploadLoading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40">
                        {uploadLoading ? "Kaydediliyor..." : `✓ Sisteme İşle (${(uploadPreview.totals?.invoice_count || 0) + (uploadPreview.totals?.payment_count || 0)} kayıt)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
